// Vista «Embudo»: una columna por etapa. Arrastrar una tarjeta a otra columna
// cambia la etapa con el PATCH existente (auditado). Sin ratón: «Mover a…» en
// cada tarjeta. En el celular se ve una etapa a la vez (sin scroll horizontal).

import { useRef, useState } from 'react';
import { Loader2, UserX } from 'lucide-react';

import type { LeadDTO } from '@/modules/leads-import';
import { Avatar, prettyPhone } from '@/modules/whatsapp-app/components/shared';
import { assigneeLabel, isUnanswered, leadTitle, originLine, shortWhen, STAGES, type StageKey } from '../model';
import { MoveMenu } from './parts';

export type BoardColumn = { rows: LeadDTO[]; total: number | null; loading: boolean; error?: string | null };

export function LeadsBoard({ columns, names, activeId, onOpen, onMove, onLoadMore, isMobile, mobileStage, onMobileStage, footnote }: {
  columns: Record<StageKey, BoardColumn>;
  names: Record<string, string>;
  activeId: string | null;
  onOpen: (l: LeadDTO) => void;
  onMove: (l: LeadDTO, s: StageKey) => void;
  onLoadMore: (s: StageKey) => void;
  isMobile: boolean;
  mobileStage: StageKey;
  onMobileStage: (s: StageKey) => void;
  footnote?: string;
}) {
  const dragged = useRef<LeadDTO | null>(null);
  const [over, setOver] = useState<StageKey | null>(null);

  const card = (l: LeadDTO) => {
    const who = assigneeLabel(l, names);
    return (
      <li key={l.id}
        draggable={!isMobile}
        onDragStart={(e) => { dragged.current = l; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', l.id); }}
        onDragEnd={() => { dragged.current = null; setOver(null); }}
        className={`la-in group rounded-xl border bg-card p-2.5 ${isMobile ? '' : 'cursor-grab active:cursor-grabbing'} shadow-sm transition-shadow hover:shadow-md ${activeId === l.id ? 'border-primary ring-1 ring-primary/40' : 'border-border'}`}>
        <div className="flex items-start gap-2">
          <button type="button" onClick={() => onOpen(l)} className="flex min-w-0 flex-1 items-start gap-2 rounded-lg text-left">
            <Avatar name={leadTitle(l)} size={30} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13.5px] font-semibold text-foreground">{leadTitle(l)}</span>
              <span className="block truncate text-[12px] text-muted-foreground">{(l.phone && prettyPhone(l.phone)) || l.email || 'Sin contacto'}</span>
            </span>
          </button>
          <MoveMenu current={l.status} onMove={(s) => onMove(l, s)} compact />
        </div>
        <div className="mt-2 flex items-center gap-2 text-[11.5px] text-muted-foreground">
          <span className="min-w-0 flex-1 truncate">{originLine(l) || 'Origen sin registrar'}</span>
          <span className="shrink-0 tabular-nums">{shortWhen(l.created_at)}</span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11.5px]">
          {who ? <span className="min-w-0 truncate text-foreground/80">{who}</span>
            : <span className="inline-flex items-center gap-1 text-muted-foreground"><UserX className="h-3 w-3" aria-hidden />Sin asignar</span>}
          {isUnanswered(l) && <span className="ml-auto shrink-0 font-medium text-destructive">Sin respuesta</span>}
        </div>
      </li>
    );
  };

  const column = (key: StageKey, label: string, tone: string, hint: string) => {
    const c = columns[key];
    const more = c.total != null && c.rows.length < c.total;
    return (
      <section key={key} aria-label={`Etapa ${label}`}
        onDragOver={(e) => { if (dragged.current && dragged.current.status !== key) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOver(key); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver((o) => (o === key ? null : o)); }}
        onDrop={(e) => { e.preventDefault(); const l = dragged.current; setOver(null); dragged.current = null; if (l && l.status !== key) onMove(l, key); }}
        className={`${tone} la-col-top flex min-h-0 min-w-0 flex-col rounded-2xl border border-border bg-muted/40 ${isMobile ? 'h-full' : 'min-w-[192px] flex-1'} ${over === key ? 'la-drop' : ''}`}>
        {!isMobile && (
          <header className="flex items-center gap-2 px-3 pb-2 pt-3" title={hint}>
            <span className="la-dot h-2 w-2 rounded-full" aria-hidden />
            <h3 className="text-[13px] font-semibold text-foreground">{label}</h3>
            <span className="la-chip ml-auto rounded-full px-2 py-0.5 text-[11.5px] font-semibold tabular-nums">
              {c.total == null ? (c.loading ? '…' : c.rows.length) : c.total.toLocaleString('es-MX')}
            </span>
          </header>
        )}
        <div className="la-scroll min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {c.error ? (
            <p className="rounded-lg bg-destructive/10 px-3 py-2 text-[12px] text-destructive">{c.error}</p>
          ) : c.rows.length === 0 && !c.loading ? (
            <p className="rounded-xl border border-dashed border-border px-3 py-6 text-center text-[12.5px] text-muted-foreground">
              {isMobile ? `Nada en ${label.toLowerCase()} con estos filtros.` : 'Arrastra aquí un lead para moverlo a esta etapa.'}
            </p>
          ) : (
            <ul className="space-y-2" aria-busy={c.loading || undefined}>{c.rows.map(card)}</ul>
          )}
          {c.loading && <p className="flex items-center gap-1.5 px-1 py-2 text-[12px] text-muted-foreground" role="status"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Cargando…</p>}
          {more && !c.loading && (
            <button type="button" onClick={() => onLoadMore(key)}
              className="mt-2 w-full rounded-lg border border-border bg-card py-1.5 text-[12.5px] font-medium text-foreground hover:bg-muted">
              Ver más ({(c.total! - c.rows.length).toLocaleString('es-MX')})
            </button>
          )}
        </div>
      </section>
    );
  };

  if (isMobile) {
    const s = STAGES.find((x) => x.key === mobileStage) || STAGES[0];
    return (
      <div className="flex h-full min-h-0 flex-col gap-2 p-3">
        <div role="tablist" aria-label="Etapas del embudo" className="grid grid-cols-5 gap-1 rounded-xl border border-border bg-muted p-1">
          {STAGES.map((x) => {
            const sel = x.key === mobileStage;
            const t = columns[x.key].total;
            return (
              <button key={x.key} type="button" role="tab" aria-selected={sel} onClick={() => onMobileStage(x.key)}
                className={`${x.tone} flex min-w-0 flex-col items-center rounded-[10px] px-0.5 py-1.5 ${sel ? 'bg-card shadow-sm' : ''}`}>
                <span className={`text-[15px] font-semibold tabular-nums ${sel ? 'la-text' : 'text-foreground'}`}>{t == null ? '…' : t > 999 ? `${Math.floor(t / 1000)}k` : t}</span>
                <span className={`max-w-full truncate text-[10.5px] ${sel ? 'text-foreground' : 'text-muted-foreground'}`}>{x.label}</span>
              </button>
            );
          })}
        </div>
        <div className="min-h-0 flex-1">{column(s.key, s.label, s.tone, s.hint)}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="la-scroll flex min-h-0 flex-1 gap-3 overflow-x-auto p-3 sm:p-4">
        {STAGES.map((s) => column(s.key, s.label, s.tone, s.hint))}
      </div>
      {footnote && <p className="border-t border-border px-4 py-2 text-[11.5px] text-muted-foreground">{footnote}</p>}
    </div>
  );
}
